import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Star, ShieldCheck, Car, Loader2, Send, MessageSquare } from 'lucide-react';
import { API } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * Public profile + booking action page.
 *
 * Route: /profile/:userId
 *  - Shows name, avatar, rating, bio, vehicle (if driver)
 *  - For passenger viewing a driver:  [Anfragen] button with optional message
 *  - For driver viewing a pending request: Accept/Reject buttons (via inbox)
 */
export default function ProfileView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: me } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentStatus, setSentStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/match/profile/${userId}`);
        if (!cancelled) setProfile(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'load_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId]);

  const sendRequest = async () => {
    if (!userId || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/match/request`, {
        driver_id: userId,
        message: message.trim() || undefined,
      });
      setSentStatus({
        ok: r.data?.ok,
        duplicate: r.data?.duplicate,
        status: r.data?.status,
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'request_failed');
    }
    setSending(false);
  };

  const isMe = me && profile && me.id === profile.id;
  const isDriverProfile = profile?.role === 'driver' || profile?.role === 'both' || !!profile?.vehicle;

  return (
    <div data-testid="profile-view-page" style={st.page}>
      <button data-testid="profile-view-back" onClick={() => navigate(-1)} style={st.backBtn}>
        <ArrowLeft size={16} /> {t('common.back', 'Zurück')}
      </button>

      {loading && (
        <div data-testid="profile-view-loading" style={st.loading}>
          <Loader2 size={20} /> {t('common.loading', 'Lade…')}
        </div>
      )}

      {error && (
        <div data-testid="profile-view-error" style={st.error}>{error}</div>
      )}

      {!loading && profile && (
        <div style={st.card} data-testid="profile-view-card">
          <div style={st.headerRow}>
            {profile.profile_picture_url
              ? <img src={profile.profile_picture_url} alt="" style={st.avatar} />
              : <div style={st.avatarFallback}>
                  {(profile.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                </div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={st.nameRow}>
                <h2 data-testid="profile-view-name" style={st.name}>{profile.name || '—'}</h2>
                {profile.verified && <ShieldCheck size={18} color="#22c55e" />}
              </div>
              <div style={st.metaRow}>
                {profile.rating != null && (
                  <span data-testid="profile-view-rating" style={st.metaItem}>
                    <Star size={14} color="#f59e0b" /> {profile.rating.toFixed(1)} ({profile.total_rides || 0})
                  </span>
                )}
                {profile.city && (
                  <span style={st.metaItem}>· {profile.city}</span>
                )}
              </div>
            </div>
          </div>

          {profile.bio && (
            <p data-testid="profile-view-bio" style={st.bio}>{profile.bio}</p>
          )}

          {profile.vehicle && (
            <div data-testid="profile-view-vehicle" style={st.vehicle}>
              <Car size={16} color="#00d4ff" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {profile.vehicle.make} {profile.vehicle.model}
                </div>
                <div style={st.vehicleMeta}>
                  {profile.vehicle.color && <span>{profile.vehicle.color}</span>}
                  {profile.vehicle.license_plate && <span>· {profile.vehicle.license_plate}</span>}
                  {profile.vehicle.segment && <span>· {profile.vehicle.segment}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Action area — only when viewing someone else's driver profile */}
          {!isMe && isDriverProfile && (
            <div style={st.actionArea}>
              {sentStatus ? (
                <div data-testid="profile-view-sent" style={{
                  ...st.sentMsg,
                  background: sentStatus.duplicate ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)',
                  color: sentStatus.duplicate ? '#fbbf24' : '#86efac',
                  border: `1px solid ${sentStatus.duplicate ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`,
                }}>
                  {sentStatus.duplicate
                    ? t('match.alreadySent', 'Anfrage läuft bereits.')
                    : t('match.requestSent', 'Anfrage gesendet — der Fahrer wird benachrichtigt.')}
                </div>
              ) : (
                <>
                  <label style={st.label}>
                    <MessageSquare size={14} /> {t('match.optionalMessage', 'Nachricht (optional)')}
                  </label>
                  <textarea data-testid="profile-view-message"
                    value={message} onChange={e => setMessage(e.target.value.slice(0, 500))}
                    placeholder={t('match.messagePlaceholder', 'Z. B. „Bin am Marienplatz, danke!"')}
                    style={st.textarea} rows={3} />
                  <button data-testid="profile-view-request" onClick={sendRequest} disabled={sending}
                    style={{ ...st.requestBtn, opacity: sending ? 0.6 : 1 }}>
                    {sending ? <Loader2 size={16} /> : <Send size={16} />}
                    {sending ? t('common.sending', 'Sende…') : t('match.request', 'Anfragen')}
                  </button>
                </>
              )}
            </div>
          )}

          {isMe && (
            <div data-testid="profile-view-self" style={st.selfHint}>
              {t('profile.thisIsYou', 'Das ist dein Profil.')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const st = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg,#0b1220 0%,#0f172a 60%,#020617 100%)',
    padding: '24px 16px', color: '#f1f5f9', position: 'relative',
  },
  backBtn: {
    position: 'absolute', top: 18, left: 18,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '6px 12px', color: '#cbd5e1', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
  },
  loading: { textAlign: 'center', padding: 60, color: '#94a3b8', display: 'flex', justifyContent: 'center', gap: 8 },
  error: { maxWidth: 480, margin: '60px auto', padding: 14, borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#fca5a5', textAlign: 'center' },
  card: {
    maxWidth: 520, margin: '60px auto 20px', padding: 26, borderRadius: 18,
    background: 'rgba(15,23,42,0.92)', border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 30px 80px rgba(2,6,23,0.55)',
  },
  headerRow: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 },
  avatar: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' },
  avatarFallback: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'linear-gradient(135deg,#0ea5e9,#00d4ff)',
    color: '#fff', fontWeight: 700, fontSize: 28,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  nameRow: { display: 'flex', alignItems: 'center', gap: 8 },
  name: { margin: 0, fontSize: 22, color: '#f1f5f9' },
  metaRow: { marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8', fontSize: 13 },
  metaItem: { display: 'flex', alignItems: 'center', gap: 4 },
  bio: { color: '#cbd5e1', fontSize: 14, lineHeight: 1.5, marginBottom: 16 },
  vehicle: {
    display: 'flex', gap: 12, padding: 14, borderRadius: 12, marginBottom: 18,
    background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
  },
  vehicleMeta: { fontSize: 12, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 4 },
  actionArea: { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 },
  label: { fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 },
  textarea: {
    width: '100%', padding: 12, borderRadius: 10, fontSize: 14,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#f1f5f9', outline: 'none', resize: 'vertical', fontFamily: 'inherit',
  },
  requestBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px', borderRadius: 12, fontSize: 14, fontWeight: 600,
    background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)', color: '#fff', border: 'none',
    cursor: 'pointer',
  },
  sentMsg: { padding: '12px 16px', borderRadius: 10, fontSize: 14, textAlign: 'center' },
  selfHint: { padding: 12, borderRadius: 10, background: 'rgba(99,102,241,0.1)', color: '#c7d2fe', fontSize: 13, textAlign: 'center' },
};
